import { CleApiErrorText } from "@/components/cle-api/CleApiErrorText";
import {
  mergeEvidenceDetailUpdate,
  mergeEvidenceDetailWithOverride,
  useAddReviewThreadComment,
  useCourseCertificate,
  useCourseCertificatePdf,
  useDownloadCourseCertificate,
  useReclassifyReviewThread,
  useReviewThreadAuditTrail,
  useReviewThreadComments,
} from "@/hooks/cle-hooks";
import type { CourseCertificateEvidenceModel } from "@/api/cle-api";
import { showCleApiErrorToast } from "@/lib/showCleApiErrorToast";
import { useEffect, useMemo, useState } from "react";
import { useToaster } from "rsuite";

import { EvidenceReviewThreadTopPanel } from "@/components/pages/efteruddannelse/EvidenceReviewThreadTopPanel";
import { ReviewDetailLayout } from "@/components/pages/efteruddannelse/ReviewDetailLayout";
import { CourseCertificateAuditTrail } from "./CourseCertificateAuditTrail";
import {
  getReclassifyLocks,
  getMissingCourseCertificateFields,
} from "./utils/approvalCompleteness";
import {
  DocumentClassificationActions,
  REPORTED_EVIDENCE_LOCKED_REASON,
} from "./DocumentClassificationActions";
import { CourseCertificateComments } from "./CourseCertificateComments";
import { CourseCertificateInformation } from "./CourseCertificateInformation";
import { EvidencePdfViewer } from "./EvidencePdfViewer";
import { CourseCertificateValidation } from "./CourseCertificateValidation";
import { SupplementaryDocuments } from "./SupplementaryDocuments";
import type { CourseCertificateDecision } from "./types";
import { useObjectUrl } from "./useObjectUrl";

interface CourseCertificateReviewViewProps {
  userId: string;
  evidenceId: string;
  onBack: () => void;
}

export const CourseCertificateReviewView = ({
  userId,
  evidenceId,
  onBack,
}: CourseCertificateReviewViewProps) => {
  const { data, isLoading, error } = useCourseCertificate({
    userId,
    id: evidenceId,
  });
  const { data: pdfBlob, isLoading: isPdfLoading } = useCourseCertificatePdf({
    userId,
    id: evidenceId,
  });
  const {
    data: auditTrail = [],
    isLoading: isAuditTrailLoading,
    error: auditTrailError,
  } = useReviewThreadAuditTrail(evidenceId);
  const {
    data: comments = [],
    isLoading: isCommentsLoading,
    error: commentsError,
  } = useReviewThreadComments(evidenceId);
  const toaster = useToaster();
  const { mutate: downloadCertificate, isPending: isDownloading } =
    useDownloadCourseCertificate();
  const { mutate: addComment, isPending: isAddingComment } =
    useAddReviewThreadComment();
  const { mutate: reclassifyCertificate, isPending: isReclassifying } =
    useReclassifyReviewThread();
  const pdfUrl = useObjectUrl(pdfBlob);
  const [certificateOverride, setCertificateOverride] = useState<
    CourseCertificateEvidenceModel | undefined
  >(undefined);

  useEffect(() => {
    setCertificateOverride(undefined);
  }, [evidenceId]);

  const certificate = useMemo(
    () => mergeEvidenceDetailWithOverride(data, certificateOverride),
    [certificateOverride, data],
  );

  const handleCertificateUpdated = (
    updated: CourseCertificateEvidenceModel,
  ) => {
    setCertificateOverride((prev) =>
      mergeEvidenceDetailUpdate(data ?? prev, updated),
    );
  };

  const handleDownloadClick = () => {
    downloadCertificate({ userId, id: evidenceId });
  };

  const handleAddComment = (content: string) => {
    addComment(
      { subjectId: evidenceId, content, userId },
      {
        onSuccess: () => setCertificateOverride(undefined),
        onError: (err) => showCleApiErrorToast(toaster, err),
      },
    );
  };

  const handleReclassify = (decision: CourseCertificateDecision) => {
    reclassifyCertificate(
      {
        subjectId: evidenceId,
        userId,
        newStatus: decision,
      },
      {
        onSuccess: () => setCertificateOverride(undefined),
        onError: (err) => showCleApiErrorToast(toaster, err),
      },
    );
  };

  return (
    <ReviewDetailLayout
      className="p-2"
      onBack={onBack}
      topPanel={
        <EvidenceReviewThreadTopPanel
          reviewThread={certificate?.reviewThread}
          ownerUserId={userId}
        />
      }
      loading={isLoading}
      error={
        error ? (
          <CleApiErrorText
            error={error}
            fallback="Kunne ikke hente kursusbevis."
          />
        ) : null
      }
      pdfPanel={
        certificate ? (
          <EvidencePdfViewer
            fileName={certificate.fileName}
            isDownloading={isDownloading}
            isLoading={isPdfLoading}
            onDownload={handleDownloadClick}
            pdfUrl={pdfUrl}
          />
        ) : null
      }
      auditPanel={
        certificate ? (
          <CourseCertificateAuditTrail
            auditTrail={auditTrail}
            certificate={certificate}
            error={auditTrailError}
            isLoading={isAuditTrailLoading}
          />
        ) : null
      }
      infoPanel={
        certificate ? (
          <>
            <CourseCertificateInformation certificate={certificate} />
            <CourseCertificateValidation
              certificate={certificate}
              onCertificateUpdated={handleCertificateUpdated}
            />
            <SupplementaryDocuments
              evidence={certificate}
              kind="kursusbevis"
              userId={userId}
            />
          </>
        ) : null
      }
      commentsPanel={
        certificate ? (
          <CourseCertificateComments
            comments={comments}
            error={commentsError}
            isAddingComment={isAddingComment}
            isLoading={isCommentsLoading}
            onAddComment={handleAddComment}
            readOnly={certificate.isReported}
          />
        ) : null
      }
      actionsPanel={
        certificate ? (
          <DocumentClassificationActions
            isPending={isReclassifying}
            onReclassify={handleReclassify}
            {...getReclassifyLocks({
              isReported: certificate.isReported,
              missingFields: getMissingCourseCertificateFields(certificate),
              reportedReason: REPORTED_EVIDENCE_LOCKED_REASON,
            })}
          />
        ) : null
      }
    />
  );
};
